import {
  createClient,
  type Session,
  type SupabaseClient,
  type User,
} from '@supabase/supabase-js';
import { AUTH_MODE, SUPABASE_ANON_KEY, SUPABASE_URL } from './config';

export interface AuthSnapshot {
  userId: string;
  email: string | null;
  metadataName: string | null;
  mode: 'supabase' | 'dev';
}

interface DevIdentity {
  uid: string;
  name: string;
}

type Listener = (snapshot: AuthSnapshot | null) => void;

const DEV_KEY = 'chroma.dev.identity';

export class AuthClient {
  readonly mode: 'supabase' | 'dev' | 'none';
  private supabase: SupabaseClient | null = null;
  private listeners = new Set<Listener>();
  private recoveryListeners = new Set<() => void>();
  private devIdentity: DevIdentity | null = null;
  private current: AuthSnapshot | null = null;

  constructor() {
    this.mode = AUTH_MODE;
    if (this.mode === 'supabase') {
      this.supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
        },
      });
    } else if (this.mode === 'dev') {
      this.devIdentity = readDevIdentity();
    }
  }

  async init(): Promise<void> {
    if (this.supabase) {
      const { data } = await this.supabase.auth.getSession();
      this.notify(data.session);
      this.supabase.auth.onAuthStateChange((event, session) => {
        this.notify(session);
        if (event === 'PASSWORD_RECOVERY') {
          for (const listener of this.recoveryListeners) listener();
        }
      });
    } else if (this.devIdentity) {
      this.emit(this.snapshotFromDev());
    }
  }

  onChange(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onPasswordRecovery(listener: () => void): () => void {
    this.recoveryListeners.add(listener);
    return () => this.recoveryListeners.delete(listener);
  }

  get snapshot(): AuthSnapshot | null {
    return this.current;
  }

  get currentUserId(): string | null {
    return this.current?.userId ?? null;
  }

  async token(): Promise<string | null> {
    if (this.devIdentity) {
      return `dev:${this.devIdentity.uid}:${encodeURIComponent(this.devIdentity.name)}`;
    }
    if (!this.supabase) return null;
    const { data } = await this.supabase.auth.getSession();
    if (!data.session) return null;
    const expiresAt = (data.session.expires_at ?? 0) * 1000;
    if (expiresAt > 0 && expiresAt - Date.now() < 60_000) {
      const refreshed = await this.supabase.auth.refreshSession();
      return refreshed.data.session?.access_token ?? null;
    }
    return data.session.access_token;
  }

  async signIn(email: string, password: string): Promise<void> {
    if (!this.supabase) throw new Error('账号服务未配置');
    const { error } = await this.supabase.auth.signInWithPassword({ email, password });
    if (error) throw new Error(translateAuthError(error.message));
  }

  async signUp(
    email: string,
    password: string,
    displayName: string,
  ): Promise<{ needsConfirmation: boolean }> {
    if (!this.supabase) throw new Error('账号服务未配置');
    const { data, error } = await this.supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) throw new Error(translateAuthError(error.message));
    return { needsConfirmation: !data.session };
  }

  async signOut(): Promise<void> {
    if (this.supabase) {
      await this.supabase.auth.signOut();
    } else {
      this.devIdentity = null;
      sessionStorage.removeItem(DEV_KEY);
      this.emit(null);
    }
  }

  devSignIn(name: string): void {
    if (this.mode !== 'dev') throw new Error('开发登录未启用');
    const identity: DevIdentity = {
      uid: crypto.randomUUID(),
      name,
    };
    this.devIdentity = identity;
    sessionStorage.setItem(DEV_KEY, JSON.stringify(identity));
    this.emit(this.snapshotFromDev());
  }

  async requestPasswordReset(email: string): Promise<void> {
    if (!this.supabase) throw new Error('账号服务未配置');
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    if (error) throw new Error(translateAuthError(error.message));
  }

  async updatePassword(password: string): Promise<void> {
    if (!this.supabase) throw new Error('开发身份不支持修改密码。');
    const { error } = await this.supabase.auth.updateUser({ password });
    if (error) throw new Error(translateAuthError(error.message));
  }

  private snapshotFromDev(): AuthSnapshot {
    return {
      userId: this.devIdentity?.uid ?? '',
      email: null,
      metadataName: this.devIdentity?.name ?? null,
      mode: 'dev',
    };
  }

  private notify(session: Session | null): void {
    if (!session) {
      this.emit(null);
      return;
    }
    this.emit({
      userId: session.user.id,
      email: session.user.email ?? null,
      metadataName: displayNameOf(session.user),
      mode: 'supabase',
    });
  }

  private emit(snapshot: AuthSnapshot | null): void {
    this.current = snapshot;
    for (const listener of this.listeners) listener(snapshot);
  }
}

function displayNameOf(user: User): string | null {
  const value = user.user_metadata?.display_name;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function readDevIdentity(): DevIdentity | null {
  try {
    const raw = sessionStorage.getItem(DEV_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<DevIdentity>;
    if (typeof parsed.uid !== 'string' || typeof parsed.name !== 'string') return null;
    return { uid: parsed.uid, name: parsed.name };
  } catch {
    return null;
  }
}

function translateAuthError(message: string): string {
  const normalized = message.toLowerCase();
  if (normalized.includes('invalid login')) return '邮箱或密码不正确。';
  if (normalized.includes('email not confirmed')) return '邮箱尚未确认，请先查看确认邮件。';
  if (normalized.includes('already registered')) return '该邮箱已经注册。';
  if (normalized.includes('password')) return '密码不满足要求。';
  if (normalized.includes('rate limit')) return '尝试过于频繁，请稍后再试。';
  return message;
}
